import { column, Schema, Table } from '@powersync/common';
/**
 * PowerSync client schema (docs/07 §4.3): exactly the tables and columns replicated by the
 * streams of infra/powersync/sync-config.yaml. Kept by hand and checked by tests against the
 * row schemas of @flow/domain and against the YAML (packages/sync/tests/schema-alignment).
 *
 * Type mapping (PowerSync only has text/integer/real): uuid, text, timestamptz, date and jsonb
 * columns are `text` (ISO strings, JSON text); booleans are `integer` (0/1). `id` is implicit.
 */
const audit = {
    created_at: column.text,
    updated_at: column.text,
};
export const workspaces = new Table({
    name: column.text,
    kind: column.text,
    owner_id: column.text,
    template_key: column.text,
    template_version: column.text,
    ai_capture_enabled: column.integer,
    deleted_at: column.text,
    ...audit,
});
export const memberships = new Table({
    workspace_id: column.text,
    user_id: column.text,
    role: column.text,
    ...audit,
}, { indexes: { by_workspace: ['workspace_id'], by_user: ['user_id'] } });
export const stages = new Table({
    workspace_id: column.text,
    key: column.text,
    name: column.text,
    category: column.text,
    kind_scope: column.text,
    color: column.text,
    icon: column.text,
    sort_key: column.text,
    is_capture_default: column.integer,
    archived_at: column.text,
    ...audit,
}, { indexes: { by_workspace: ['workspace_id'] } });
export const taxonomies = new Table({
    workspace_id: column.text,
    key: column.text,
    name: column.text,
    prefix: column.text,
    cardinality: column.text,
    inherited: column.integer,
    is_lens: column.integer,
    sort_key: column.text,
    ...audit,
}, { indexes: { by_workspace: ['workspace_id'] } });
export const tags = new Table({
    workspace_id: column.text,
    taxonomy_id: column.text,
    parent_id: column.text,
    name: column.text,
    color: column.text,
    icon: column.text,
    user_id: column.text,
    merged_into_id: column.text,
    sort_key: column.text,
    archived_at: column.text,
    deleted_at: column.text,
    ...audit,
}, { indexes: { by_taxonomy: ['taxonomy_id'], by_parent: ['parent_id'] } });
export const blocks = new Table({
    workspace_id: column.text,
    parent_id: column.text,
    sort_key: column.text,
    kind: column.text,
    title: column.text,
    body: column.text,
    stage_id: column.text,
    stage_changed_at: column.text,
    status: column.text,
    due_on: column.text,
    start_on: column.text,
    remind_at: column.text,
    recurrence: column.text,
    recurrence_anchor: column.text,
    recurrence_series_id: column.text,
    completed_at: column.text,
    assignee_id: column.text,
    created_by: column.text,
    capture_proposal_id: column.text,
    props: column.text,
    archived_at: column.text,
    deleted_at: column.text,
    ...audit,
}, {
    indexes: {
        tree: ['workspace_id', 'parent_id', 'sort_key'],
        // Watched lists ordered by sort key across parents read the workspace through this index
        // instead of every row (f1-local-write-latency: each local save waits for those re-runs).
        by_workspace_sort: ['workspace_id', 'sort_key'],
        // Recursive inheritance walks the tree by parent alone (docs/08 §6).
        by_parent: ['parent_id'],
        // Stage counters use the (stage, status) prefix; the Inbox list and the triage queue read it
        // oldest first through created_at, with no temporary sort (f1-triage, design D4).
        by_stage: ['stage_id', 'status', 'created_at'],
        // Completed tasks of a stage within a window (ASAP «hechas en este periodo», f1-asap-view D1):
        // the open ones go through by_stage, these through their completion date.
        by_stage_completed: ['stage_id', 'completed_at'],
        by_due: ['due_on'],
        // The Trash lists the trashed blocks of a workspace (f1-trash-search, design D2).
        by_trash: ['workspace_id', 'deleted_at'],
    },
});
export const block_tags = new Table({
    workspace_id: column.text,
    block_id: column.text,
    tag_id: column.text,
    taxonomy_id: column.text,
    ...audit,
}, { indexes: { by_block_taxonomy: ['block_id', 'taxonomy_id'], by_tag: ['tag_id'] } });
/** Local-only cache of inherited tags (docs/08 §6, §10). Rebuilt by packages/sync/effective-tags. */
export const effective_tags = new Table({
    block_id: column.text,
    taxonomy_id: column.text,
    tag_id: column.text,
    inherited_from: column.text,
}, {
    localOnly: true,
    indexes: { by_taxonomy_tag: ['taxonomy_id', 'tag_id'], by_block: ['block_id', 'taxonomy_id'] },
});
export const periods = new Table({
    user_id: column.text,
    started_at: column.text,
    closed_at: column.text,
    ritual_run_id: column.text,
    summary: column.text,
    ...audit,
}, { indexes: { by_user: ['user_id', 'started_at'] } });
export const sync_rejections = new Table({
    user_id: column.text,
    table_name: column.text,
    row_id: column.text,
    op: column.text,
    reason_code: column.text,
    message: column.text,
    created_at: column.text,
    acknowledged_at: column.text,
    updated_at: column.text,
}, { indexes: { by_user: ['user_id'] } });
export const user_preferences = new Table({
    user_id: column.text,
    theme: column.text,
    density: column.text,
    locale: column.text,
    timezone: column.text,
    week_start: column.integer,
    default_workspace_id: column.text,
    ...audit,
});
export const field_defs = new Table({
    workspace_id: column.text,
    key: column.text,
    label: column.text,
    type: column.text,
    kind_scope: column.text,
    options: column.text,
    required: column.integer,
    sort_key: column.text,
    archived_at: column.text,
    ...audit,
}, { indexes: { by_workspace: ['workspace_id'] } });
export const field_values = new Table({
    workspace_id: column.text,
    block_id: column.text,
    field_id: column.text,
    value_text: column.text,
    value_number: column.real,
    value_date: column.text,
    value_ts: column.text,
    value_bool: column.integer,
    value_ref: column.text,
    value_json: column.text,
    ...audit,
}, { indexes: { by_block: ['block_id'], by_field: ['field_id', 'value_date'] } });
/** `extracted_text` stays on the server (docs/08 §9); the client only gets `search_text`. */
export const assets = new Table({
    workspace_id: column.text,
    block_id: column.text,
    capture_id: column.text,
    role: column.text,
    storage_key: column.text,
    mime: column.text,
    bytes: column.integer,
    sha256: column.text,
    phash: column.text,
    width: column.integer,
    height: column.integer,
    duration_ms: column.integer,
    thumb_asset_id: column.text,
    search_text: column.text,
    text_source: column.text,
    upload_status: column.text,
    created_by: column.text,
    deleted_at: column.text,
    ...audit,
}, { indexes: { by_block: ['block_id'], by_workspace: ['workspace_id'] } });
export const views = new Table({
    user_id: column.text,
    workspace_id: column.text,
    key: column.text,
    name: column.text,
    icon: column.text,
    sidebar_section: column.text,
    sort_key: column.text,
    scope: column.text,
    filter: column.text,
    layout: column.text,
    group_by: column.text,
    sort: column.text,
    options: column.text,
    is_system: column.integer,
    template_key: column.text,
    deleted_at: column.text,
    ...audit,
}, { indexes: { by_user: ['user_id', 'sort_key'], by_workspace: ['workspace_id', 'sort_key'] } });
export const highlight_types = new Table({
    user_id: column.text,
    key: column.text,
    name: column.text,
    label: column.text,
    icon: column.text,
    color: column.text,
    max_per_period: column.integer,
    limit_mode: column.text,
    eligible_kind: column.text,
    eligible_stage_category: column.text,
    pin_top: column.integer,
    ...audit,
}, { indexes: { by_user: ['user_id'] } });
export const highlights = new Table({
    user_id: column.text,
    highlight_type_id: column.text,
    block_id: column.text,
    period_id: column.text,
    rank: column.integer,
    achieved_at: column.text,
    removed_at: column.text,
    ...audit,
}, { indexes: { by_period: ['user_id', 'period_id'], by_block: ['block_id'] } });
export const rituals = new Table({
    user_id: column.text,
    key: column.text,
    name: column.text,
    schedule: column.text,
    closes_period: column.integer,
    steps: column.text,
    ...audit,
}, { indexes: { by_user: ['user_id'] } });
export const ritual_runs = new Table({
    user_id: column.text,
    ritual_id: column.text,
    step_state: column.text,
    started_at: column.text,
    completed_at: column.text,
    ...audit,
}, { indexes: { by_ritual: ['ritual_id', 'started_at'] } });
export const syncSchema = new Schema({
    user_preferences,
    workspaces,
    memberships,
    stages,
    taxonomies,
    tags,
    field_defs,
    blocks,
    block_tags,
    field_values,
    assets,
    views,
    highlight_types,
    rituals,
    ritual_runs,
    periods,
    highlights,
    sync_rejections,
    effective_tags,
});
/** Names of the replicated tables declared in the client schema (local-only tables excluded). */
export function clientSchemaTables() {
    return Object.entries(syncSchema.props)
        .filter(([, table]) => !table.localOnly)
        .map(([name]) => name);
}
/** Names of the local-only tables (docs/08 §10). */
export function localOnlyTables() {
    return Object.entries(syncSchema.props)
        .filter(([, table]) => table.localOnly)
        .map(([name]) => name);
}
/** Column names of a client table (without the implicit `id`). */
export function clientSchemaColumns(table) {
    const def = syncSchema.props[table];
    return def ? def.columns.map((c) => c.name) : [];
}
//# sourceMappingURL=schema.js.map